import { useState } from "react";
import { Search, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function OrderLookup() {
  const [email, setEmail] = useState("");
  const [orderId, setOrderId] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !orderId) return;
    setLoading(true);
    try {
      const r = await api.post("/orders/lookup", { email, order_id: orderId });
      setOrder(r.data);
    } catch {
      toast.error("Order not found");
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div data-testid="order-lookup-page" className="max-w-3xl mx-auto px-6 py-16">
      <div className="font-mono text-xs uppercase tracking-widest text-[#00FF66]">// orders</div>
      <h1 className="font-heading font-bold text-4xl mt-2">Order lookup</h1>
      <p className="text-[#A1A1AA] mt-2">Enter the email used at checkout and your order ID.</p>

      <form onSubmit={submit} className="surface p-6 mt-8 space-y-4">
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">email</label>
          <input data-testid="lookup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-term" required />
        </div>
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-[#A1A1AA] block mb-2">order id</label>
          <input data-testid="lookup-order-id" value={orderId} onChange={(e) => setOrderId(e.target.value.toUpperCase())} placeholder="GS-XXXXXXXX" className="input-term" required />
        </div>
        <button data-testid="lookup-submit" disabled={loading} className="btn-primary"><Search className="w-4 h-4" />{loading ? "looking up..." : "find order"}</button>
      </form>

      {order && (
        <div data-testid="order-result" className="surface p-6 mt-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-mono text-[#00FF66]">{order.id}</div>
              <div className="text-xs text-[#A1A1AA] font-mono mt-0.5">{order.email}</div>
            </div>
            <div className="flex gap-2">
              <span className={`chip-mono ${order.payment_status === "paid" ? "" : "!bg-[#F59E0B]/10 !text-[#F59E0B] !border-[#F59E0B]/30"}`}>{order.payment_status}</span>
              <span className={`chip-mono ${order.order_status === "completed" ? "" : "!bg-white/5 !text-[#A1A1AA] !border-white/10"}`}>{order.order_status}</span>
            </div>
          </div>
          <div className="mt-5 border-t border-[#1E293B] pt-5">
            <div className="text-sm font-mono text-[#A1A1AA] mb-2">items</div>
            {order.items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm py-1">
                <span>{it.name} × {it.quantity}</span>
                <span className="font-mono">${(it.price * it.quantity).toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between font-mono text-sm mt-2 pt-2 border-t border-[#1E293B]">
              <span>total</span><span className="text-[#00FF66] font-bold">${order.total.toFixed(2)}</span>
            </div>
          </div>
          {order.license_keys?.length > 0 && (
            <div className="mt-6">
              <div className="text-sm font-mono text-[#A1A1AA] mb-2">license keys</div>
              {order.license_keys.map((k, i) => (
                <div key={i} className="border border-[#1E293B] rounded-md p-3 bg-[#0a0a0f] flex items-center justify-between gap-3 mt-2">
                  <div>
                    <div className="text-xs text-[#A1A1AA] font-mono">{k.product_name}</div>
                    <code className="font-mono text-[#00FF66] text-sm break-all">{k.key}</code>
                  </div>
                  <button onClick={() => copy(k.key)} className="btn-ghost shrink-0"><Copy className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
